import type { ScheduledQuotesActivity } from 'apiConnector/scheduledRun/model';
import type { Action } from 'state/actions';
import { onColumnStateChanged } from 'state/blotters/common/blotter.reducer.utils';
import type {
  ScheduledQuotesBlotterColumnDefinitionReceived,
  ScheduledQuotesBlotterFilterChanged,
  ScheduledQuotesBlotterItemsReceived,
  ScheduledQuotesBlotterState,
} from 'state/blotters/scheduledQuotes';
import { initialScheduledQuotesBlotterState } from 'state/blotters/scheduledQuotes';

// tslint:disable-next-line:cyclomatic-complexity
export function scheduledQuotesBlotterReducer(
  state: ScheduledQuotesBlotterState = initialScheduledQuotesBlotterState,
  action: Action,
): ScheduledQuotesBlotterState {
  switch (action.type) {
    case 'ROUTE_CHANGED':
      return { ...state, selectedRowId: undefined };
    case 'SCHEDULED_RUN_DELETE_SCHEDULE_SUCCEEDED':
      return { ...state, selectedRowId: action.runId === state.selectedRowId ? undefined : state.selectedRowId };
    case 'SCHEDULED_QUOTES_BLOTTER_COLUMN_DEFINITION_RECEIVED':
      return onColumnDefinitionReceived(state, action);
    case 'SCHEDULED_QUOTES_BLOTTER_SELECTION_CHANGED':
      return {
        ...state,
        selectedRowId: action.selectedRowId,
      };
    case 'SCHEDULED_QUOTES_BLOTTER_COLUMN_STATE_CHANGED':
      return onColumnStateChanged(state, action);
    case 'SCHEDULED_QUOTES_BLOTTER_PANEL_TAB_CHANGED':
      return { ...state, selectedPanelTab: action.tab };
    case 'SCHEDULED_QUOTES_BLOTTER_FILTERS_LOADED':
      return { ...state, ...action.filters };
    case 'SCHEDULED_QUOTES_BLOTTER_FILTER_CHANGED':
      return onFilterChanged(state, action);
    case 'SCHEDULED_QUOTES_BLOTTER_PREFERENCE_STATE_GET':
      return { ...state, areBlotterPreferencesInError: false };
    case 'SCHEDULED_QUOTES_BLOTTER_PREFERENCE_STATE_LOADED':
      return {
        ...state,
        areBlotterPreferencesInError: false,
        canSaveBlotterPreferences: true,
        blotterPreferenceState: { ...state.blotterPreferenceState, ...action.blotterPreferenceState },
      };
    case 'SCHEDULED_QUOTES_BLOTTER_ITEMS_REQUESTED':
      return { ...state, error: false, loading: true };
    case 'SCHEDULED_QUOTES_BLOTTER_ITEMS_RECEIVED':
      return onScheduledQuotesBlotterItemsReceived(state, action);
    case 'SCHEDULED_QUOTES_BLOTTER_ITEMS_FETCHING_FAILED':
      return { ...state, error: true, loading: false };
    case 'SCHEDULED_QUOTES_BLOTTER_ITEM_RECEIVED':
      return { ...state, items: state.items.map(item => (item.id === action.item.id ? action.item : item)) };
    default:
      return state;
  }
}

function onColumnDefinitionReceived(
  state: ScheduledQuotesBlotterState,
  { columnDefinition }: ScheduledQuotesBlotterColumnDefinitionReceived,
): ScheduledQuotesBlotterState {
  return {
    ...state,
    columnDefinition,
    sort: [],
  };
}

function onScheduledQuotesBlotterItemsReceived(
  state: ScheduledQuotesBlotterState,
  { items, totalItems, totalPages }: ScheduledQuotesBlotterItemsReceived,
): ScheduledQuotesBlotterState {
  const hasSelectedRowId = items.some(item => item.id === state.selectedRowId);
  return {
    ...state,
    error: false,
    loading: false,
    items,
    selectedRowId: hasSelectedRowId ? state.selectedRowId : undefined,
    totalItems,
    totalPages,
  };
}

function onFilterChanged(
  state: ScheduledQuotesBlotterState,
  { filter, value }: ScheduledQuotesBlotterFilterChanged,
): ScheduledQuotesBlotterState {
  switch (filter) {
    case 'activityFilter':
      return {
        ...state,
        filters: {
          ...state.filters,
          activityFilter: value as ScheduledQuotesActivity,
        },
      };
    case 'quickSearchTerm':
      return {
        ...state,
        filters: {
          ...state.filters,
          quickSearchTerm: value,
        },
      };
    default:
      return state;
  }
}
